/**
 * F09 v2 — Approve dialog (E23 / E27 / E9).
 *
 *   - initial request whose criteria include `government_id` → ID number
 *     (masked + reveal), ID type, issuing country (default LB) AND the
 *     attestation are required;
 *   - name_change → only the attestation;
 *   - optional internal note (10–500).
 *
 * The attestation shows BOTH the entity name snapshot and the human owner's
 * name snapshot ("ID matches the human owner's name").
 *
 * 409 DUPLICATE_IDENTITY → lists the matches and offers "Acknowledge duplicate
 * and approve" (re-sends with `acknowledgeDuplicate: true`). The acknowledgement
 * is an informed override of THOSE matches only: it is tied to the exact ID
 * inputs that produced them, so editing the ID number / type / country drops
 * it and the next submit re-runs the duplicate check without the override.
 * The ID number is held in this component's state only — never logged or
 * stored — and is dropped when the dialog unmounts.
 */
import { useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import { Banner, Button, Checkbox, FormField, Textarea } from '@bravemove/ui-web';
import {
  VERIFICATION_INTERNAL_NOTE_MAX,
  VERIFICATION_INTERNAL_NOTE_MIN,
  type AdminApproveVerificationInput,
  type AdminVerificationRequestDetail,
  type VerificationDuplicateMatch,
} from '@bravemove/types';
import { adminVerificationApi } from '../../api/verification';
import { FormModal } from './FormModal';
import { lengthError, noErrors } from './validation';
import { IdDocumentFields } from './IdDocumentFields';
import {
  COUNTRY_RE,
  EMPTY_ID_DOCUMENT,
  type IdDocumentErrors,
  type IdDocumentValues,
} from './idDocument';
import { approveNeedsIdFields, entityLabel, requestStatusLabel } from './labels';
import { duplicateMatchesOf, verificationErrorCode } from './verificationErrors';

export function DuplicateMatchList({
  matches,
  testId,
}: {
  readonly matches: readonly VerificationDuplicateMatch[];
  readonly testId: string;
}) {
  const { t } = useI18n();
  return (
    <ul className="space-y-1.5" data-testid={testId}>
      {matches.map((m) => (
        <li
          key={`${m.matchKind}:${m.requestId}:${m.docId ?? ''}`}
          className="rounded-md border border-hairline bg-surface-2 px-3 py-2 text-[13px]"
          data-same-subject={m.sameSubject ? 'true' : 'false'}
        >
          <span className="font-semibold text-ink">
            {m.entityName ?? m.entityId} · {entityLabel(t, m.entityType)}
          </span>
          <span className="text-ink-3">
            {' — '}
            {t(`admin:verification.panels.matchKind.${m.matchKind}`)} ·{' '}
            {requestStatusLabel(t, m.requestStatus)} ·{' '}
            {m.sameSubject
              ? t('admin:verification.panels.sameSubject')
              : t('admin:verification.panels.otherSubject')}
          </span>
        </li>
      ))}
    </ul>
  );
}

/** The duplicate matches the server returned, and the exact ID inputs they were computed for. */
interface DuplicateCheck {
  readonly matches: readonly VerificationDuplicateMatch[];
  /** Identity-compared: every edit produces a new values object. */
  readonly forIdDoc: IdDocumentValues;
}

export function ApproveDialog({
  detail,
  onClose,
  onApproved,
}: {
  readonly detail: AdminVerificationRequestDetail;
  readonly onClose: () => void;
  readonly onApproved: () => void;
}) {
  const { t } = useI18n();
  const needsId = approveNeedsIdFields(detail);
  const isNameChange = detail.requestKind === 'name_change';

  const [idDoc, setIdDoc] = useState<IdDocumentValues>(EMPTY_ID_DOCUMENT);
  const [attested, setAttested] = useState(false);
  const [internalNote, setInternalNote] = useState('');
  const [idErrors, setIdErrors] = useState<IdDocumentErrors>({});
  const [attestError, setAttestError] = useState<string | undefined>(undefined);
  const [noteError, setNoteError] = useState<string | undefined>(undefined);
  const [submitError, setSubmitError] = useState<unknown>(null);
  const [duplicateCheck, setDuplicateCheck] = useState<DuplicateCheck | null>(null);
  const [busy, setBusy] = useState(false);

  // The acknowledgement is only offered (and only sent) while the inputs are
  // still the ones the shown matches were computed for. This also covers a
  // 409 that lands after the admin edited the fields mid-request.
  const duplicates =
    duplicateCheck !== null && duplicateCheck.forIdDoc === idDoc ? duplicateCheck.matches : null;

  const changeIdDoc = (next: IdDocumentValues) => {
    setIdDoc(next);
    setDuplicateCheck(null);
  };

  const validate = (): boolean => {
    const nextId: IdDocumentErrors = needsId
      ? {
          number:
            idDoc.number.trim().length === 0
              ? t('admin:verification.validation.idNumber')
              : undefined,
          type: idDoc.type === '' ? t('admin:verification.validation.idType') : undefined,
          country: COUNTRY_RE.test(idDoc.country)
            ? undefined
            : t('admin:verification.validation.country'),
        }
      : {};
    const nextAttest = attested ? undefined : t('admin:verification.validation.attestation');
    const nextNote = lengthError(
      t,
      internalNote,
      VERIFICATION_INTERNAL_NOTE_MIN,
      VERIFICATION_INTERNAL_NOTE_MAX,
      false,
    );
    setIdErrors(nextId);
    setAttestError(nextAttest);
    setNoteError(nextNote);
    return noErrors({ ...nextId, attest: nextAttest, note: nextNote });
  };

  const submit = async (acknowledgeDuplicate: boolean) => {
    if (!validate()) return;
    const submittedIdDoc = idDoc;
    const acknowledge = acknowledgeDuplicate && duplicates !== null;
    const note = internalNote.trim();
    const body: AdminApproveVerificationInput = {
      nameMatchConfirmed: true,
      ...(needsId && idDoc.type !== ''
        ? {
            idDocumentNumber: idDoc.number.trim(),
            idDocumentType: idDoc.type,
            idDocumentIssuingCountry: idDoc.country,
          }
        : {}),
      ...(note.length > 0 ? { internalNote: note } : {}),
      ...(acknowledge ? { acknowledgeDuplicate: true } : {}),
    };
    setBusy(true);
    setSubmitError(null);
    try {
      await adminVerificationApi.approve(detail.id, body);
      // Drop the ID number from memory before handing control back.
      setIdDoc(EMPTY_ID_DOCUMENT);
      setDuplicateCheck(null);
      onApproved();
    } catch (err) {
      if (verificationErrorCode(err) === 'DUPLICATE_IDENTITY') {
        setDuplicateCheck({ matches: duplicateMatchesOf(err), forIdDoc: submittedIdDoc });
      } else {
        setSubmitError(err);
      }
    } finally {
      setBusy(false);
    }
  };

  const showAck = duplicates !== null;

  return (
    <FormModal
      title={
        isNameChange
          ? t('admin:verification.approve.nameChangeTitle')
          : t('admin:verification.approve.title')
      }
      description={
        isNameChange
          ? t('admin:verification.approve.nameChangeDescription')
          : t('admin:verification.approve.description')
      }
      onClose={onClose}
      onConfirm={() => void submit(false)}
      confirmLabel={t('admin:verification.approve.confirm')}
      busy={busy}
      error={submitError}
      testId="approve-dialog"
      hideConfirm={showAck}
      footerExtra={
        showAck ? (
          <Button
            variant="destructive"
            loading={busy}
            onClick={() => void submit(true)}
            data-testid="approve-dialog-acknowledge"
          >
            {t('admin:verification.approve.acknowledgeAndApprove')}
          </Button>
        ) : null
      }
    >
      {showAck ? (
        <div data-testid="approve-dialog-duplicates">
          <Banner tone="warning" title={t('admin:verification.approve.duplicateTitle')}>
            {t('admin:verification.approve.duplicateBody')}
          </Banner>
          <div className="mt-2">
            <DuplicateMatchList matches={duplicates} testId="approve-dialog-duplicate-matches" />
          </div>
        </div>
      ) : null}

      {needsId ? (
        <IdDocumentFields
          values={idDoc}
          onChange={changeIdDoc}
          errors={idErrors}
          required
          testIdPrefix="approve-dialog"
        />
      ) : null}

      <dl
        className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 rounded-md border border-hairline bg-surface-2 px-3 py-2 text-[13px]"
        data-testid="approve-dialog-names"
      >
        <dt className="text-ink-3">{t('admin:verification.approve.entityName')}</dt>
        <dd className="font-semibold text-ink" data-testid="approve-dialog-entity-name">
          {detail.entityNameSnapshot ?? detail.currentEntityName}
        </dd>
        <dt className="text-ink-3">{t('admin:verification.approve.humanName')}</dt>
        <dd className="font-semibold text-ink" data-testid="approve-dialog-human-name">
          {detail.identitySubjectNameSnapshot ?? detail.subjectDisplayName ?? '—'}
        </dd>
        {isNameChange ? (
          <>
            <dt className="text-ink-3">{t('admin:verification.approve.requestedName')}</dt>
            <dd className="font-semibold text-ink">{detail.requestedName ?? '—'}</dd>
          </>
        ) : null}
      </dl>

      <FormField error={attestError}>
        <Checkbox
          checked={attested}
          invalid={!!attestError}
          onChange={(e) => setAttested(e.target.checked)}
          data-testid="approve-dialog-attestation"
          label={
            isNameChange
              ? t('admin:verification.approve.nameChangeAttestation')
              : t('admin:verification.approve.attestation')
          }
        />
      </FormField>

      <FormField
        label={t('admin:verification.approve.internalNote')}
        helper={t('admin:verification.approve.internalNoteHelper')}
        error={noteError}
      >
        <Textarea
          value={internalNote}
          maxLength={VERIFICATION_INTERNAL_NOTE_MAX}
          invalid={!!noteError}
          data-testid="approve-dialog-internal-note"
          onChange={(e) => setInternalNote(e.target.value)}
        />
      </FormField>
    </FormModal>
  );
}
